import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  faPlus,
  faSearch,
  faArrowRight,
  faFolderOpen,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CreateProjectPopup from "../../components/CreateProjectPopup";
import { useProjects } from "../../queries/use-projects";

const ACCENTS = [
  { from: "#66CCCE", to: "#2A8589" },
  { from: "#FABE98", to: "#F46F36" },
  { from: "#A78BFA", to: "#6D28D9" },
  { from: "#FDA4AF", to: "#E11D48" },
  { from: "#86EFAC", to: "#059669" },
  { from: "#FCD34D", to: "#D97706" },
  { from: "#7DD3FC", to: "#0369A1" },
];

const hashCode = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) % 2147483647;
  }
  return h;
};

const accentFor = (name) => ACCENTS[hashCode(name) % ACCENTS.length];

const initialsFor = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const ProjectCard = ({ project }) => {
  const accent = accentFor(project.name || "");

  return (
    <Link
      to={`/projects/${project.id}`}
      className="group relative block rounded-2xl bg-white border border-gray-200 overflow-hidden transform hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300 transition"
    >
      <div
        className="h-2 w-full"
        style={{
          background: `linear-gradient(90deg, ${accent.from}, ${accent.to})`,
        }}
      />
      <div className="p-5 flex items-center gap-4">
        <div
          className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-base shadow-sm"
          style={{
            background: `linear-gradient(135deg, ${accent.from}, ${accent.to})`,
          }}
        >
          {initialsFor(project.name)}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-gray-900 text-base leading-tight truncate">
            {project.name || "Untitled"}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Project #{project.id}</p>
        </div>
        <FontAwesomeIcon
          icon={faArrowRight}
          className="text-gray-300 group-hover:text-primary transform group-hover:translate-x-0.5 transition"
        />
      </div>
    </Link>
  );
};

const CardSkeleton = () => (
  <div className="rounded-2xl bg-white border border-gray-200 overflow-hidden">
    <div className="h-2 bg-gray-100" />
    <div className="p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-gray-100" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-3/4 bg-gray-100 rounded" />
        <div className="h-3 w-1/4 bg-gray-100 rounded" />
      </div>
    </div>
  </div>
);

const EmptyState = ({ onCreate, searching }) => (
  <div className="text-center py-20 px-6 rounded-2xl bg-white border border-dashed border-gray-300">
    <div className="mx-auto w-14 h-14 rounded-2xl bg-primary bg-opacity-10 text-primary flex items-center justify-center">
      <FontAwesomeIcon icon={faFolderOpen} className="text-2xl" />
    </div>
    <h3 className="mt-4 text-lg font-semibold text-gray-900">
      {searching ? "No matching projects" : "No projects yet"}
    </h3>
    <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
      {searching
        ? "Try a different search term, or create a new project."
        : "Create your first project to start managing translations."}
    </p>
    <button
      type="button"
      onClick={onCreate}
      className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-white text-sm font-semibold px-5 py-2.5 shadow-sm hover:opacity-95 transition"
    >
      <FontAwesomeIcon icon={faPlus} className="w-3.5 h-3.5" />
      New project
    </button>
  </div>
);

const AllProjects = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [query, setQuery] = useState("");
  const { isLoading, isError, data: projects, error } = useProjects();

  const filtered = useMemo(() => {
    if (!projects) return [];
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [projects, query]);

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            Projects
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {isLoading
              ? "Loading your projects…"
              : `${projects?.length ?? 0} project${
                  (projects?.length ?? 0) === 1 ? "" : "s"
                }`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects"
              className="pl-9 pr-3 py-2 w-full sm:w-64 rounded-full border border-gray-200 bg-white text-sm placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-20 transition"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-white text-sm font-semibold px-5 py-2.5 shadow-sm hover:opacity-95 transition whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faPlus} className="w-3.5 h-3.5" />
            New project
          </button>
        </div>
      </div>

      {isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-800 p-4 flex items-start gap-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
          <div>
            <p className="font-semibold">Something went wrong</p>
            <p className="text-sm text-red-700 mt-0.5">
              {error?.message || "Failed to load projects."}
            </p>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      )}

      {!isLoading && !isError && filtered.length === 0 && (
        <EmptyState
          onCreate={() => setShowCreateForm(true)}
          searching={query.trim().length > 0}
        />
      )}

      {!isLoading && !isError && filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}

      {showCreateForm && (
        <CreateProjectPopup onClose={() => setShowCreateForm(false)} />
      )}
    </>
  );
};

export default AllProjects;
